import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { RealtimeChannel } from '@supabase/supabase-js';
import type { Tables } from '@auctisync/shared';
import { supabase } from '../lib/supabase';
import { useSession } from '../hooks/useSession';

type Notification = Tables<'notifications'>;

const MAX_SHOWN = 10;

/**
 * In-app notification bell. Subscribes to postgres_changes on
 * `public.notifications` filtered by user_id for live inserts and
 * read_at updates; falls back to a one-shot fetch on mount for initial
 * state. Closes on outside click or Escape.
 */
export function NotificationBell() {
  const { session } = useSession();
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement | null>(null);

  const [items, setItems] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const userId = session?.user?.id ?? null;
  const unreadCount = items.filter((n) => n.read_at === null).length;

  // Initial load
  useEffect(() => {
    if (!userId) {
      setItems([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(MAX_SHOWN);
      if (cancelled) return;
      if (!error && data) setItems(data as Notification[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Realtime subscription for live inserts + read_at updates
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    let channel: RealtimeChannel | null = null;

    const subscribe = () => {
      if (cancelled) return;
      channel = supabase
        .channel(`notifications-${userId}-${Date.now()}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`,
          },
          (payload) => {
            if (cancelled) return;
            const row = payload.new as Notification;
            setItems((prev) => {
              // Guard against double-delivery (realtime + initial load race)
              if (prev.some((x) => x.id === row.id)) return prev;
              return [row, ...prev].slice(0, MAX_SHOWN);
            });
          },
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`,
          },
          (payload) => {
            if (cancelled) return;
            const row = payload.new as Notification;
            setItems((prev) => prev.map((x) => (x.id === row.id ? row : x)));
          },
        )
        .subscribe();
    };

    subscribe();

    return () => {
      cancelled = true;
      if (channel) void supabase.removeChannel(channel);
    };
  }, [userId]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const markAllRead = useCallback(async () => {
    if (!userId) return;
    const now = new Date().toISOString();
    const unreadIds = items.filter((n) => n.read_at === null).map((n) => n.id);
    if (unreadIds.length === 0) return;
    // Optimistic
    setItems((prev) => prev.map((n) => (n.read_at === null ? { ...n, read_at: now } : n)));
    await supabase
      .from('notifications')
      .update({ read_at: now })
      .in('id', unreadIds)
      .eq('user_id', userId);
  }, [userId, items]);

  const onItemClick = useCallback(
    async (n: Notification) => {
      setOpen(false);
      if (n.read_at === null) {
        const now = new Date().toISOString();
        // Optimistic
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read_at: now } : x)));
        await supabase.from('notifications').update({ read_at: now }).eq('id', n.id);
      }
      if (n.href) void navigate(n.href);
    },
    [navigate],
  );

  if (!session) return null;

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications'}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          position: 'relative',
          background: 'transparent',
          border: '1px solid var(--border)',
          borderRadius: '0.375rem',
          padding: '0.3rem 0.55rem',
          cursor: 'pointer',
          fontSize: '1rem',
          lineHeight: 1,
          color: 'var(--text)',
        }}
      >
        <span aria-hidden="true">🔔</span>
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '-0.3rem',
              right: '-0.3rem',
              minWidth: '1.1rem',
              height: '1.1rem',
              padding: '0 0.3rem',
              borderRadius: '999px',
              background: 'var(--semantic-error, #dc2626)',
              color: 'white',
              fontSize: '0.7rem',
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 0 2px var(--surface-elev)',
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 0.4rem)',
            width: 'min(22rem, 90vw)',
            maxHeight: '70vh',
            overflowY: 'auto',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '0.5rem',
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
            zIndex: 50,
          }}
        >
          <div
            style={{
              padding: '0.65rem 0.85rem',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: '0.5rem',
            }}
          >
            <strong style={{ fontSize: '0.85rem', color: 'var(--text)' }}>Notifications</strong>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  void markAllRead();
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--brand-accent)',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Mark all read
              </button>
            )}
          </div>
          {loading ? (
            <div style={emptyStyle}>Loading…</div>
          ) : items.length === 0 ? (
            <div style={emptyStyle}>No notifications yet.</div>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {items.map((n) => {
                const unread = n.read_at === null;
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => {
                        void onItemClick(n);
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        background: unread ? 'var(--surface-elev)' : 'transparent',
                        border: 'none',
                        borderBottom: '1px solid var(--border)',
                        padding: '0.6rem 0.85rem',
                        cursor: 'pointer',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          gap: '0.4rem',
                          alignItems: 'baseline',
                          marginBottom: '0.15rem',
                        }}
                      >
                        {unread && (
                          <span
                            aria-label="unread"
                            style={{
                              width: '0.45rem',
                              height: '0.45rem',
                              borderRadius: '999px',
                              background: 'var(--brand-accent)',
                              display: 'inline-block',
                              flexShrink: 0,
                              marginTop: '0.4rem',
                            }}
                          />
                        )}
                        <div
                          style={{
                            fontSize: '0.85rem',
                            fontWeight: unread ? 600 : 500,
                            color: 'var(--text)',
                            lineHeight: 1.3,
                          }}
                        >
                          {n.title}
                        </div>
                      </div>
                      {n.body && (
                        <div
                          style={{
                            fontSize: '0.8rem',
                            color: 'var(--text-muted)',
                            lineHeight: 1.4,
                            marginBottom: '0.2rem',
                            paddingLeft: unread ? '0.85rem' : 0,
                          }}
                        >
                          {n.body}
                        </div>
                      )}
                      <time
                        style={{
                          fontSize: '0.7rem',
                          color: 'var(--text-muted)',
                          paddingLeft: unread ? '0.85rem' : 0,
                        }}
                        dateTime={n.created_at}
                      >
                        {formatRelative(n.created_at)}
                      </time>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

const emptyStyle: React.CSSProperties = {
  padding: '1.5rem 1rem',
  textAlign: 'center',
  color: 'var(--text-muted)',
  fontSize: '0.85rem',
};

function formatRelative(iso: string): string {
  const dt = new Date(iso).getTime();
  const diff = Date.now() - dt;
  const min = 60_000;
  const hr = 60 * min;
  const day = 24 * hr;
  if (diff < min) return 'just now';
  if (diff < hr) return `${Math.floor(diff / min)}m ago`;
  if (diff < day) return `${Math.floor(diff / hr)}h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return new Date(iso).toLocaleDateString();
}
